import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBars } from '@fortawesome/free-solid-svg-icons';
import { createPortal } from 'react-dom';
import Nav from 'react-bootstrap/Nav';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { logout } from '../services/axiosInstance';
import { getBankAccounts } from '../services/BankAccountService';
import axiosInstance from '../services/axiosInstance';
import '../css/MobileHeader.css';
import { useAuth } from '../contexts/AuthContext';
import { useNavigationPreferences } from '../contexts/NavigationPreferencesContext';
import { getTenant } from '../utils/tenantUtils';

function MobileHeader({ handleShow, pageTitle }) {
  const { isAuthenticated } = useAuth();
  const { mobileHomePath } = useNavigationPreferences();
  const navigate = useNavigate();
  const tenant = getTenant();
  const [showMenu, setShowMenu] = useState(false);
  const [logoUrl, setLogoUrl] = useState('/static/logos/budgit_logo.png');
  const [bankAccounts, setBankAccounts] = useState([]);
  const [accountsWithBills, setAccountsWithBills] = useState([]);
  const [trackNickname, setTrackNickname] = useState('Bills');
  const [showBillsDropdown, setShowBillsDropdown] = useState(false);
  const menuRef = useRef(null);

  const getTenantPath = (path) => {
    if (tenant) {
      return `/$/${tenant}${path}`;
    }
    return path;
  };

  useEffect(() => {
    const fetchUserLogo = async () => {
      try {
        const response = await axiosInstance.get('/tenant-logo-settings/');
        const logoPath = `/static/logos/${response.data.logo_filename}`;
        setLogoUrl(logoPath);
      } catch (error) {
        console.error('Error fetching user logo:', error.name);
      }
    };

    if (isAuthenticated) {
      fetchUserLogo();
    }

    const handleLogoUpdate = () => fetchUserLogo();
    window.addEventListener('logoUpdated', handleLogoUpdate);

    return () => window.removeEventListener('logoUpdated', handleLogoUpdate);
  }, [isAuthenticated]);

  useEffect(() => {
    const fetchBankAccounts = async () => {
      try {
        const accounts = await getBankAccounts();
        if (accounts && accounts.length > 0) {
          setBankAccounts(accounts);

          const trackedAccount = accounts.find((account) => account.include_in_tracking);
          if (trackedAccount) {
            setTrackNickname('Bills');
          }

          setAccountsWithBills(accounts);
        }
      } catch (error) {
        console.error('Error fetching bank accounts:', error.name);

        if (error.response && error.response.status === 401) {
          setTimeout(() => {
            if (isAuthenticated) {
              fetchBankAccounts();
            }
          }, 2000);
        }
      }
    };

    if (isAuthenticated) {
      fetchBankAccounts();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (!showMenu) return undefined;

    const onPointerDown = (event) => {
      if (
        menuRef.current?.contains(event.target) ||
        event.target.closest?.('.mobile-menu-fab')
      ) {
        return;
      }
      setShowMenu(false);
      setShowBillsDropdown(false);
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown);

    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
    };
  }, [showMenu]);

  const closeMenu = () => {
    setShowMenu(false);
    setShowBillsDropdown(false);
  };

  const handleBillsClick = () => {
    setShowBillsDropdown(!showBillsDropdown);
  };

  const handleBankAccountClick = (accountId) => {
    navigate(getTenantPath(`/track/${accountId}`));
    closeMenu();
  };

  const handleAllBillsClick = () => {
    navigate(getTenantPath('/track'));
    closeMenu();
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Navigation: Error during logout:', error.name);
    }
  };

  if (!isAuthenticated) {
    return null;
  }

  const logoSrc = `${window.location.protocol}//${window.location.host}${logoUrl}`;

  const floatingChrome =
    typeof document !== 'undefined'
      ? createPortal(
          <>
            <Link
              to={mobileHomePath}
              className="mobile-logo-fab"
              aria-label="Go to home"
              title="Home"
            >
              <img alt="" src={logoSrc} className="mobile-logo-fab-img" />
            </Link>

            <button
              type="button"
              className="mobile-menu-fab"
              onClick={() => setShowMenu((open) => !open)}
              aria-label={showMenu ? 'Close menu' : 'Open menu'}
              aria-expanded={showMenu}
              aria-haspopup="true"
            >
              <FontAwesomeIcon icon={faBars} />
            </button>

            {showMenu && (
              <div className="mobile-nav-menu-panel" ref={menuRef} role="menu">
                <div className="mobile-navbar-nav-items">
                  <div className="mobile-nav-section">
                    <div className="mobile-nav-header" onClick={handleBillsClick}>
                      <span>{trackNickname}</span>
                      <span className={`dropdown-arrow ${showBillsDropdown ? 'open' : ''}`}>▼</span>
                    </div>

                    {showBillsDropdown && (
                      <div className="mobile-dropdown">
                        <div className="mobile-dropdown-item" onClick={handleAllBillsClick}>
                          All Bills
                        </div>
                        {accountsWithBills &&
                          accountsWithBills.length > 0 &&
                          accountsWithBills.map((account) => (
                            <div
                              key={account.id}
                              className="mobile-dropdown-item"
                              onClick={() => handleBankAccountClick(account.id)}
                            >
                              {account.nickname || account.name}
                            </div>
                          ))}
                      </div>
                    )}
                  </div>

                  <Nav.Link as={Link} to={getTenantPath('/transfers')} onClick={closeMenu}>
                    Transfers
                  </Nav.Link>
                  <Nav.Link as={Link} to={getTenantPath('/requirements')} onClick={closeMenu}>
                    Requirements
                  </Nav.Link>
                  <Nav.Link
                    as={NavLink}
                    to={getTenantPath('/settings')}
                    className="nav-link"
                    onClick={closeMenu}
                  >
                    Settings
                  </Nav.Link>
                  {handleShow && (
                    <Nav.Link onClick={() => { handleShow(); closeMenu(); }} className="nav-link">
                      More
                    </Nav.Link>
                  )}
                  <Nav.Link onClick={handleLogout} className="nav-link">
                    Logout
                  </Nav.Link>
                </div>
              </div>
            )}
          </>,
          document.body
        )
      : null;

  return (
    <>
      {pageTitle ? <span className="visually-hidden">{pageTitle}</span> : null}
      {floatingChrome}
    </>
  );
}

export default MobileHeader;
